import { DotLottieReact, setWasmUrl, type DotLottie, type LoadErrorEvent } from '@lottiefiles/dotlottie-react';
// Serve the renderer's WASM from our own bundle. The default fetches it from a public CDN,
// which the app's CSP blocks and which fails offline. `?url` emits a hashed, version-synced asset.
import dotLottieWasmUrl from '@lottiefiles/dotlottie-web/dotlottie-player.wasm?url';
import { type CSSProperties, useCallback, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { cn } from '@/lib/utils';

setWasmUrl(dotLottieWasmUrl);

export interface AccessibleLottieProps {
    /** Lottie JSON animation data. When absent, the static fallback image is shown instead. */
    animationData?: unknown;
    /** Static image shown for reduced motion, load failure, or missing data. */
    fallbackSrc?: string;
    alt: string;
    /** Whether the animation loops. Defaults to `true`. */
    loop?: boolean;
    /**
     * How many times a non-looping animation plays before `onComplete` fires. The renderer runs
     * them as one continuous playback, so there is no rebuild and no gap between plays.
     */
    playCount?: number;
    className?: string;
    style?: CSSProperties;
    /**
     * Fires when a non-looping animation reaches its last frame. Never fires when the fallback image
     * is showing so callers that advance state on completion need their own timer backstop.
     */
    onComplete?: () => void;
    /**
     * Hides the graphic from assistive tech entirely instead of exposing `alt` as its name. Use
     * when the lottie sits inside an already-labeled control so the button isn't announced twice.
     */
    presentational?: boolean;
}

/**
 * Renders a Lottie animation with an accessible name, falling back to a static image when the user prefers
 * reduced motion, no animation data is available, or the animation fails to load.
 *
 * Uses the dotLottie (ThorVG/WASM) renderer rather than lottie-web: it plays image- and precomp-heavy
 * animations far more smoothly, which matches what LottieFiles' own preview shows.
 *
 * The accessible name lives on the wrapper (`role="img"` + `aria-label`). The animation and fallback image
 * are marked `aria-hidden` so screen readers announce the illustration exactly once.
 */
export const AccessibleLottie = ({
    animationData,
    fallbackSrc,
    alt,
    loop = true,
    playCount = 1,
    className,
    style,
    onComplete,
    presentational = false,
}: AccessibleLottieProps) => {
    const prefersReducedMotion = usePrefersReducedMotion();
    // A load/render failure in the WASM player flips this so the static image takes over — the illustration is never blank.
    const [failed, setFailed] = useState(false);
    const canAnimate = animationData != null && !prefersReducedMotion && !failed;

    // Held in a ref so a fresh onComplete closure each render doesn't re-register the listener.
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const handleLoadError = useCallback((event: LoadErrorEvent) => {
        console.error('Lottie animation failed to load; showing fallback image.', event);
        setFailed(true);
    }, []);

    const handleComplete = useCallback(() => onCompleteRef.current?.(), []);

    // Tracked so each player is listened to exactly once. A remount that reuses the canvas gets the
    // same instance back rather than a new one, which is what StrictMode does on every mount, and a
    // second `complete` listener on it would report every play twice.
    const playerRef = useRef<DotLottie | null>(null);

    const handleDotLottieRef = useCallback((dotLottie: DotLottie | null) => {
        if (playerRef.current === dotLottie) {
            return;
        }
        playerRef.current?.removeEventListener('loadError', handleLoadError);
        playerRef.current?.removeEventListener('complete', handleComplete);
        playerRef.current = dotLottie;
        dotLottie?.addEventListener('loadError', handleLoadError);
        dotLottie?.addEventListener('complete', handleComplete);
    }, [handleLoadError, handleComplete]);

    const fallbackImage = fallbackSrc
        ? (
                <img
                    src={fallbackSrc}
                    alt=""
                    aria-hidden
                    className="block h-full w-full object-contain"
                    decoding="async"
                    loading="eager"
                />
            )
        : null;

    // dotLottie counts replays after the first, and honors none of them unless `loop` is on.
    const replayCount = loop ? 0 : Math.max(0, playCount - 1);

    return (
        <div
            role={presentational ? undefined : 'img'}
            aria-label={presentational ? undefined : alt}
            aria-hidden={presentational || undefined}
            className={cn(
                'h-full w-full',
                // The player renders a bare <canvas>. Being inline-replaced it sits on the text
                // baseline and leaves the descender gap showing beneath it however tall the
                // wrapper is. On any other background that reads as a sliver of color.
                '[&_canvas]:block',
                className,
            )}
            style={style}
        >
            {canAnimate
                ? (
                        <DotLottieReact
                            data={animationData as Record<string, unknown>}
                            loop={loop || replayCount > 0}
                            loopCount={replayCount}
                            autoplay
                            aria-hidden
                            className="h-full w-full"
                            // autoResize keeps the canvas matched to the container and freezeOnOffscreen pauses it when scrolled away.
                            renderConfig={{ autoResize: true, freezeOnOffscreen: true }}
                            dotLottieRefCallback={handleDotLottieRef}
                        />
                    )
                : fallbackImage}
        </div>
    );
};
